<template>
  <v-layout row wrap class="ma-1">
    <v-flex xs12 md1>
      <img :src="shipmentSourceImg" :width="imgWidth" />
      {{ sourceOrder }}
    </v-flex>

    <v-flex xs12 md4>
      <ShipmentAddress :title="$t('origin')" :address="origin"></ShipmentAddress>
    </v-flex>

    <v-flex xs12 md4>
      <ShipmentAddress :title="$t('destination')" :address="destination"></ShipmentAddress>
    </v-flex>

    <v-flex xs12 md3>
      <img v-if="shipment.rate" :src="carrierLogo" :alt="shipment.rate.carrier_name" width="100" />
      <br />
      <a v-if="trackable" :href="trackingUrl" target="_blank">
        {{ shipment.generated_label.tracking_number }}
      </a>
      <font-awesome-icon v-if="trackable" class="ml-2" icon="copy" :color="iconColor" cursor="pointer" title="copy tracking number" size="1x" @click="copyTrackingNumber"></font-awesome-icon>
    </v-flex>

    <v-flex xs12 md4>
      <span v-if="$auth.isAdmin() || $auth.isMainAdmin() || $auth.isDev()">
        {{ $tc('models.user', 1) }}:
        <br />
        <font-awesome-icon v-if="shipment.user_prices > 0" icon="check" color="green"></font-awesome-icon>
        <font-awesome-icon v-else icon="times" color="red"></font-awesome-icon>

        {{ shipment.user.id}} / {{ shipment.user.fullname }} / {{ shipment.user.email }}
      </span>
    </v-flex>

    <v-flex xs12 md4>
      <Status :shipment="shipment"></Status>
    </v-flex>

    <v-flex xs12 md4>
      <span v-if="shipment.pickup && shipment.pickup.status == 'generated' && shipment.pickup.reference_number">
        <label>{{ $t('pickupNumber') }}:</label>
        {{ shipment.pickup.reference_number }}
      </span>
      <br>
      <span v-if="shipment.generated_label">
        <label>{{ $tc('models.service', 1) }}:</label>
        {{ shipment.rate.service.name }}
      </span>
      <br>
      <span v-if="parcel">
        <label>{{ $t('attrs.contents') }}:</label>
        {{ parcel.contents }}
        <br>
        <label>{{ $t('attrs.size') }}:</label>
        {{ shipmentPackage }}
        <br>
        <span v-if="parcel.volumetric_weight">
          <label>{{ $t('volumetricWeight') }}:</label>
          {{ parcel.volumetric_weight + ' kg' }}
        </span>
      </span>
      <br>
      <span v-if="shipment.generated_label">
        <label>{{ $t('attrs.created_at') }}:</label>
        {{ shipment.generated_label.created_at | date('lll') }}
      </span>
      <br>
      <span v-if="shipment.rate">
        <label>{{ $t('attrs.total') }}:</label>
        {{ shipment.rate.total | currency }}
      </span>
    </v-flex>

    <v-flex xs12 class="text-md-right">
      <Clone :shipment="shipment" @fetch="fetch"></Clone>
      <Cancel :shipment="shipment" @fetch="fetch"></Cancel>
      <Reject :shipment="shipment" @fetch="fetch"></Reject>
      <Pickup :shipment="shipment"></Pickup>
      <Deliver :shipment="shipment" @fetch="fetch"></Deliver>
      <Delete :shipment="shipment" @fetch="fetch"></Delete>
    </v-flex>
  </v-layout>
</template>

<script>
import Status from '@/components/shipments/btns/status'
import ShipmentAddress from '@/components/shipments/address'

import Clone from '@/components/shipments/btns/clone'
import Cancel from '@/components/shipments/btns/cancel'
import Deliver from '@/components/shipments/btns/deliver'
import Pickup from '@/components/shipments/btns/pickup'
import Delete from '@/components/shipments/btns/delete'
import Reject from '@/components/shipments/btns/reject'

export default {
  name: 'ShipmentDetailsComp',

  props: {
    shipment: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      iconColor: process.env.VUE_APP_COLOR
    }
  },

  components: {
    Status,
    ShipmentAddress,

    Clone,
    Cancel,
    Deliver,
    Pickup,
    Delete,
    Reject
  },

  computed: {
    origin () {
      return this.shipment.origin
    },

    destination () {
      return this.shipment.destination
    },

    trackable () {
      if (!this.shipment.generated_label) { return }
      if (!this.shipment.rate) { return }
      if (!this.shipment.rate.service) { return }

      return this.shipment.generated_label && this.shipment.rate.service.trackable
    },

    trackingUrl () {
      return this.shipment.generated_label.tracking_url
    },

    carrierLogo () {
      if (!this.shipment) { return '' }
      if (!this.shipment.rate) { return '' }
      if (!this.shipment.rate.service) { return '' }
      if (!this.shipment.rate.service.carrier) { return '' }
      if (!this.shipment.rate.service.carrier.logo) { return '' }
      if (!this.shipment.rate.service.carrier.logo.url) { return '' }
      return this.shipment.rate.service.carrier.logo.url
    },

    shipmentSourceImg () {
      const imgs = {
        'system': this.logoUrl,
        'app': this.logoUrl,
        'shopify': '/shopify-logo.png',
        'woocommerce': '/woocommerce-logo.png'
      }
      return imgs[this.shipment.source]
    },

    imgWidth () {
      const src = this.shipmentSourceImg
      const img = new Image()
      img.src = src
      if (img.naturalHeight > img.naturalWidth) {
        return 20
      }
      return 50
    },

    sourceOrder () {
      if (this.shipment.source == 'app') { return }
      return `#${this.shipment.source_number}`
    },

    parcel () {
      if (!this.shipment) { return }
      if (!this.shipment.parcels) { return }

      return this.shipment.parcels[0]
    },

    shipmentPackage () {
      return `(${this.parcel.length} cm x ${this.parcel.width} cm x ${this.parcel.height} cm) x ${this.parcel.weight} kg`
    }
  },

  methods: {
    fetch () {
      this.$emit('fetch')
    },

    copyTrackingNumber () {
      const el = document.createElement('textarea');
      el.value = this.shipment.generated_label.tracking_number;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);

      this.setNotification({
        color: 'success',
        message: this.$t('copiedTrackingNumber')
      })
    }
  },

  watch: {
  },

  async mounted () {}
}
</script>
